<template>
  <main-layout
    class="pro-order-list-page"
    :page-title="text.orderListTitle"
    custom-back
    @listen-back="goBack"
  >
    <div slot="main-content" class="main-content">
      <div v-if="orders.length" class="order-list">
        <order-list-item
          v-for="item in orders"
          :key="item.id"
          :order="item"
          @cancel="handleCancel"
          @pay="handlePay"
          @expired="handleExpired"
        />
      </div>
      <div v-else v-loading="loading" class="empty-box">
        <el-empty :description="text.emptyText"></el-empty>
      </div>

      <pagiation
        :total="total"
        :page-size="query.pageSize"
        :page-no="query.pageNo"
        :pagis-show="total > query.pageSize"
        @currentChange="currentChange"
      />

      <pay-dialogs
        :failedVisible.sync="failedVisible"
        @repay="handleRepay"
        @view-order="failedVisible = false"
      />
    </div>
  </main-layout>
</template>

<script>
import MainLayout from "@/layout/MainLayout";
import OrderListItem from "./components/OrderListItem.vue";
import PayDialogs from "./components/PayDialogs.vue";
import Pagiation from "@/components/Pagiation.vue";
import {
  PRO_PLAN_TEXT,
  ORDER_PAGE_SIZE,
  PRO_PLAN_ERROR,
  ORDER_STATUS,
  ORDER_SOURCE,
  ORDER_EXPIRE_CONFIRM_ATTEMPTS,
  ORDER_EXPIRE_CONFIRM_INTERVAL,
} from "@/consts/proPlan";
import {
  getProOrderList,
  cancelProOrder,
  getPendingProOrder,
  getProOrderDetail,
} from "@/api/request/snkd.proPlan";
import { remainMs, capPayCountdown } from "@/utils/proPlan";

export default {
  name: "ProPlanOrderList",
  components: { MainLayout, OrderListItem, PayDialogs, Pagiation },
  data() {
    return {
      text: PRO_PLAN_TEXT,
      orders: [],
      total: 0,
      loading: false,
      query: {
        companyId: this.$route.query.companyId,
        pageNo: 1,
        pageSize: ORDER_PAGE_SIZE,
      },
      failedVisible: false,
      // 正在核对状态的订单（orderId → true），避免卡片周期复查时并发重复请求
      expireChecking: {},
    };
  },
  created() {
    this.getList();
  },
  methods: {
    async getList() {
      this.loading = true;
      try {
        const res = await getProOrderList(this.query);
        console.log("=========", res, this.query);
        const data = res.obj || {};
        // 接口已按下单时间倒序返回；此处不再二次排序，避免顺序与后端不一致
        this.orders = data.list || [];
        this.total = data.totalCount || 0;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    currentChange(pageNo) {
      this.query.pageNo = pageNo;
      this.getList();
      // 切页后回到顶部：列表页内容可能很长，保持原滚动位置会让用户落在
      // 新一页的中间、看不到开头（禅道 Bug#23442）。
      // 滚动容器是 MainLayout 的 .el-main（overflow: auto），不是 window ——
      // window.scrollTo 在此无效，必须设置容器自身的 scrollTop。
      this.scrollToTop();
    },
    /**
     * 把列表页滚动容器复位到顶部。
     *
     * ⚠️ 容器 `.el-main` 是本组件 `$el`（`.el-container`）的**后代**，不是祖先 ——
     * 不能用 `$el.closest('.el-main')`（closest 只向上找祖先，会永远返回 null），
     * 必须用 `querySelector` 向下找。
     */
    scrollToTop() {
      // 等 DOM 更新完再滚动，避免新数据渲染前就被重置（体验上会闪一下）
      this.$nextTick(() => {
        const main =
          this.$el && this.$el.querySelector
            ? this.$el.querySelector(".el-main")
            : null;
        if (main) {
          main.scrollTop = 0;
        } else if (typeof window !== "undefined") {
          window.scrollTo(0, 0);
        }
      });
    },
    /**
     * 取消待支付订单（paymentOrder/cancel）
     * - 成功（orderStatus=30）→ 提示并刷新列表
     * - 42003（订单已支付）→ 提示不可取消并刷新，让状态更新为「已支付」
     */
    async handleCancel(order) {
      const confirmed = await this.$confirm(
        this.text.cancelOrderConfirm,
        this.text.unpaidOrderTitle,
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      ).catch(() => false);
      if (!confirmed) return;

      try {
        this.loading = true;
        const res = await cancelProOrder({
          companyId: this.$route.query.companyId,
          orderId: order.orderId,
        });
        if (res.respCode === 0) {
          this.$message.success(this.text.cancelSuccess);
        } else if (res.respCode === PRO_PLAN_ERROR.ORDER_PAID) {
          // 已被支付，无法取消；刷新后状态会变为「已支付」
          this.$message.warning(this.text.orderAlreadyPaid);
        } else if (res.respCode === PRO_PLAN_ERROR.ORDER_NOT_EXIST) {
          // 订单不存在/不属于当前企业（如列表数据已过期）
          this.$message.warning(this.text.orderNotExist);
        } else {
          this.$message.error(res.respDesc || this.text.cancelFailed);
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
        // 无论结果如何都刷新，保证状态与服务端一致
        this.getList();
      }
    },
    /**
     * 去支付（禅道 #23497）
     *
     * list 返回的 CoOrderVO 不含 codeUrl，无法直接渲染二维码；
     * 故先调 pending 取回该待支付单的 codeUrl/expireTime 再进确认页。
     *
     * ⚠️ **App 端下单的待支付单在 web 端付不了**：App 走的是「App 内拉起微信支付」
     * （下单返回 prepayInfo，由 App SDK 调起），与 web 扫码用的 codeUrl 不是一回事，
     * web 端取不到可渲染的二维码。
     * 因此先按**订单来源**分流，直接给明确提示，而不是落到「请重新选择套餐」这类
     * 泛化文案上（用户无从判断该怎么办）。
     * 注：`orderSource` 只有**订单详情**返回，列表接口不返回，故这里要多取一次详情。
     */
    async handlePay(order) {
      try {
        this.loading = true;
        // App 端下的单：web 端无法支付，直接提示（不必再调 pending）
        const isAppOrder = await this.checkIsAppOrder(order);
        if (isAppOrder) {
          this.$message.warning(this.text.appOrderCannotPayInWeb);
          return;
        }
        const res = await getPendingProOrder({
          companyId: this.$route.query.companyId,
        });
        const pending = res.obj;
        if (pending && pending.codeUrl) {
          this.$router.push({
            path: "/proPlan/orderConfirm",
            query: {
              companyId: this.$route.query.companyId,
              orderId: pending.orderId,
              codeUrl: pending.codeUrl,
              expireTime: pending.expireTime,
              orderNo: pending.orderNo,
              // 开通企业名称：设计稿确认页「开通企业」需展示，列表行已有则带入，
              // 避免等详情接口返回前出现空档（详情返回后仍会校正）
              companyName: order.companyName,
              // 套餐信息从列表行带入（pending 不返回套餐字段）
              planName: order.goodsName,
              months: order.months,
              price: order.price,
            },
          });
          return;
        }
        this.$message.warning(this.text.reorderFromList);
        this.getList();
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    /**
     * 该订单是否由 App 端下单（orderSource = 2）
     * 详情接口失败时返回 false（不阻断既有支付流程，退回原兜底提示）
     */
    async checkIsAppOrder(order) {
      if (!order || !order.orderId) return false;
      try {
        const res = await getProOrderDetail({
          companyId: this.$route.query.companyId,
          orderId: order.orderId,
        });
        const detail = res && res.obj;
        return !!detail && Number(detail.orderSource) === ORDER_SOURCE.APP;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    /**
     * 待支付倒计时归零：向后端核对真实状态后再更新卡片，不能直接本地判定过期。
     *
     * 两个必须核对的原因（禅道 Bug#23436）：
     *  1. 后端到期关单**不是瞬时的**——实测订单 14:06:56 到期，14:07:19 列表接口仍返回
     *     「待支付」，再过一会儿才变「已取消」（另一笔实测延迟约 9s）。只刷一次列表往往
     *     拿到的还是旧状态，卡片就停在 00′00″、按钮也不消失。
     *  2. 前端倒计时是推算值（列表接口不返回支付截止时间），测试期还被 capPayCountdown
     *     压短，可能与后端真实截止时间不一致；若后端仍待支付且时间未到，应回填真实剩余
     *     时长继续倒计时，而不是误判过期。
     *
     * 注意订单**详情**在待支付时不下发支付截止时间（`entitlementEnd` 为 null），
     * 真实截止时间只在 `pending` 接口里，故待支付分支需额外取一次 pending。
     */
    async handleExpired(order) {
      const orderId = order && (order.orderId || order.id);
      if (!orderId) return;
      // 卡片会在核对未果后按间隔再次触发；同一订单不并发核对
      if (this.expireChecking[orderId]) return;
      this.$set(this.expireChecking, orderId, true);
      try {
        for (let i = 0; i < ORDER_EXPIRE_CONFIRM_ATTEMPTS; i++) {
          let res = null;
          try {
            res = await getProOrderDetail({
              companyId: this.query.companyId,
              orderId,
            });
          } catch (e) {
            console.log(e);
          }

          const idx = this.orders.findIndex(
            (o) => (o.orderId || o.id) === orderId
          );
          if (idx === -1) return;

          if (res && res.respCode === 0 && res.obj) {
            const fresh = res.obj;
            if (fresh.status === ORDER_STATUS.UNPAID) {
              // 仍待支付 → 用 pending 下的真实截止时间算剩余，继续倒计时
              const realRemain = await this.fetchPendingRemainMs(orderId);
              if (realRemain > 0) {
                this.$set(this.orders, idx, {
                  ...this.orders[idx],
                  expireRemainMs: realRemain,
                });
                return;
              }
              // 取不到（已不是最新待支付单）或确实已到截止时间 → 关单可能仍在处理，重试
            } else {
              // 已支付 / 已取消 / 已关闭 / 已退款 → 终态。
              // 这里整页刷新而不是直接用详情对象替换：详情接口的 cancelTime 可能落后于
              // 列表（实测取消后 detail 仍为 null、list 已有值），直接替换会丢掉「取消时间」列
              await this.getList();
              return;
            }
          }

          // 核对未果（请求失败 / 后端仍报待支付）：退回整页刷新，并等待后再试
          await this.getList();
          if (i < ORDER_EXPIRE_CONFIRM_ATTEMPTS - 1) {
            await new Promise((r) =>
              setTimeout(r, ORDER_EXPIRE_CONFIRM_INTERVAL)
            );
          }
        }
      } finally {
        this.$set(this.expireChecking, orderId, false);
      }
    },
    /**
     * 取指定订单的真实剩余支付时长（毫秒）。
     * pending 只返回最新一笔待支付单，故仅在 orderId 匹配时采用；
     * 取不到返回 0，交由调用方按「无法确认」处理。
     */
    async fetchPendingRemainMs(orderId) {
      try {
        const res = await getPendingProOrder({
          companyId: this.query.companyId,
        });
        const p = res.obj;
        if (!p || String(p.orderId) !== String(orderId) || !p.expireTime) {
          return 0;
        }
        const remain = remainMs(p.expireTime, res.curDate);
        return isNaN(remain) ? 0 : capPayCountdown(remain);
      } catch (e) {
        console.log(e);
        return 0;
      }
    },
    handleRepay() {
      this.failedVisible = false;
    },
    goBack() {
      window.history.length > 1 ? this.$router.back() : window.history.back();
    },
  },
};
</script>

<style lang="scss" scoped>
.main-content {
  min-height: 100%;
  padding: 40px 0;
  box-sizing: border-box;
  background: #f7f9fb;
}
.order-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}
.empty-box {
  padding-top: 120px;
}
</style>
